// components/landing/IoTLabSection.tsx
'use client';

import React from 'react';
import { Cpu, Thermometer, Droplets, Sun, Wind, Zap, Activity, Wifi } from 'lucide-react';

const iotDevices = [
  { name: 'ESP32', count: 24, status: 'online', icon: Cpu, color: 'purple' },
  { name: '온도 센서', count: 48, status: 'online', icon: Thermometer, color: 'red' },
  { name: '습도 센서', count: 48, status: 'online', icon: Droplets, color: 'blue' },
  { name: '조도 센서', count: 24, status: 'online', icon: Sun, color: 'yellow' },
];

export default function IoTLabSection() {
  return (
    <section className="relative bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-purple-900/20 overflow-hidden py-24 lg:py-32">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* 오른쪽: IoT 장비 현황 */}
          <div className="order-2 lg:order-1">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">IoT 장비 현황</h3>
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-500 animate-pulse" />
                  <span className="text-sm text-green-500 font-medium">전체 온라인</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {iotDevices.map((device, index) => {
                  const Icon = device.icon;
                  const colors = {
                    purple: 'from-purple-500 to-pink-500',
                    red: 'from-red-500 to-orange-500',
                    blue: 'from-blue-500 to-cyan-500',
                    yellow: 'from-yellow-500 to-orange-500',
                  };

                  return (
                    <div
                      key={index}
                      className="bg-gradient-to-br p-4 rounded-xl  shadow-lg"
                      style={{
                        backgroundImage: `linear-gradient(135deg, var(--tw-gradient-stops))`,
                      }}
                    >
                      <Icon className="w-8 h-8 mb-2 opacity-80" />
                      <div className="text-2xl font-bold">{device.count}</div>
                      <div className="text-sm opacity-90">{device.name}</div>
                    </div>
                  );
                })}
              </div>

              {/* 실시간 데이터 스트림 */}
              <div className="bg-gray-900 dark:bg-black rounded-lg p-4 font-mono text-xs">
                <div className="text-green-400 mb-2">$ tail -f /var/log/iot-devices.log</div>
                <div className="space-y-1 text-gray-400">
                  <div>[2026-01-04 10:23:45] ESP32-01: temp=24.5°C humidity=65%</div>
                  <div>[2026-01-04 10:23:46] ESP32-02: temp=23.8°C humidity=68%</div>
                  <div>[2026-01-04 10:23:47] ESP32-03: light=35000lux co2=850ppm</div>
                  <div className="text-green-400 animate-pulse">▊</div>
                </div>
              </div>
            </div>
          </div>

          {/* 왼쪽: 설명 */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-700 dark:text-purple-300 text-sm font-medium mb-6">
              <Cpu className="w-4 h-4 mr-2" />
              IoT 인프라
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              ESP32 기반
              <span className="block text-purple-600 dark:text-purple-400 mt-2">스마트 IoT 시스템</span>
            </h2>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              자체 개발한 ESP32 기반 IoT 장비로 농장 환경을 실시간 모니터링합니다. 온도, 습도, 조도, CO₂ 농도 등 다양한 센서 데이터를
              수집합니다.
            </p>

            {/* 기능 리스트 */}
            <div className="space-y-4 mb-8">
              {[
                { icon: Wifi, text: '무선 센서 네트워크', desc: 'Wi-Fi 6 지원' },
                { icon: Zap, text: '저전력 설계', desc: '배터리 6개월 작동' },
                { icon: Activity, text: '실시간 모니터링', desc: '1초 단위 데이터 수집' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <Icon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">{item.text}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 기술 스택 */}
            <div className="flex flex-wrap gap-2">
              {['ESP32', 'FreeRTOS', 'MQTT', 'LoRaWAN', 'BLE'].map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>

            <button
              onClick={() => window.open('http://devplus.kr:3000', '_blank', 'noopener,noreferrer')}
              className="group justify-end mt-10 px-8 py-4  text-white   bg-purple-600  hover:bg-purple-700 font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 transform hover:-translate-y-1 flex items-center gap-2"
            >
              devplus+ 바로가기
              {/* <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /> */}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
